import { createColumnHelper } from '@tanstack/react-table';
import { ConfirmModal, createDataCollectionContext, dbTimeToUserTime, toaster, useMobileContext, useValidatedSorting, type ConfirmModalHandle, type ItemAction } from '@shared';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button, Text, Flex, Box } from '@chakra-ui/react';
import { useDrawer } from '../../../../context/DrawerContext';
import groupsService from '../../../../api/services/groupsService';
import { GroupDrawer } from './GroupDrawer';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth, useDeleteMultipleGroups } from '@admin';
import { IoPeopleOutline } from 'react-icons/io5';

const columnHelper = createColumnHelper<Group>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export const Groups = () => {
	const { t } = useTranslation();
	const { openDrawer } = useDrawer();
	const { currentAccount } = useAuth();
	const { isMobile } = useMobileContext();
	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = useMemo(() => createDataCollectionContext<Group>(), []);

	const columns = [
		columnHelper.accessor('name', {
			header: t('general.name'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('contactGroupsCount', {
			header: t('general.number_of_contacts'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return dbTimeToUserTime(row.original.timeCreated, TIMEZONE);
			},
		}),
		columnHelper.accessor('timeModified', {
			header: t('general.time_modified'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeModified) {
					return null;
				}
				return <Text>{dbTimeToUserTime(row.original.timeModified, TIMEZONE)}</Text>;
			},
		}),
	];

	useValidatedSorting('groups', columns);

	const confirmRemoveRef = useRef<ConfirmModalHandle>(null);
	const { mutate: deleteMultipleGroups } = useDeleteMultipleGroups();
	const queryClient = useQueryClient();

	const selectedItemsActions: Array<ItemAction<Group>> = [
		{
			id: 'deleteMany',
			label: t('general.delete_selected'),
			callbackFn: async (ids, table) => {
				const payload = {
					groups: ids.map(id => {
						return {
							groupID: parseInt(id),
							delete: true,
						};
					}),
				};

				const response = await confirmRemoveRef.current?.open({
					title: t('general.remove_groups'),
					content: t('general.really_remove_groups'),
					confirmButtonText: t('general.remove'),
					confirmButtonVariant: 'subtle',
				});

				if (response === true) {
					deleteMultipleGroups(payload, {
						onSuccess: () => {
							toaster.create({
								type: 'success',
								title: t('general.removed'),
							});
							queryClient.invalidateQueries({ queryKey: ['groups'] });
							queryClient.invalidateQueries({ queryKey: ['groups-with-contact-counts'] });

							table.resetRowSelection();
						},
					});
				}
			},
		},
	];

	return (
		<Card.Root flex={1} display="flex">
			<Card.Body display="flex" flexDirection="column" padding={5} gap={5} overflow={isMobile ? 'initial' : 'auto'}>
				<DataCollectionContextProvider
					dataKey="groups"
					customQueryKey="groups-with-contact-counts"
					keyAttribute="groupID"
					emptyDataText={t('general.no_groups_found')}
					emptyDataIcon={IoPeopleOutline}
					fetchFn={(...args) => groupsService.getAllResources(...args)}
					queryParams={{
						getRelated: 'contactGroups',
						countRelated: 'contactGroups',
					}}
					initialItemsPerPage={25}
					columns={columns}
					isServerSide={true}
					selectedItemsActions={selectedItemsActions}
					isSelectable={true}
					defaultSort={{ id: 'timeCreated', desc: true }}
					onClickRow={row => {
						if (!currentAccount) {
							return;
						}
						openDrawer({
							id: 'groupDrawer',
							content: <GroupDrawer currentAccount={currentAccount} groupID={row.original.groupID} />,
						});
					}}
				>
					<Flex gap={2} flexWrap="wrap" flexShrink={0}>
						<DataCollectionTableHeader />
						<Button
							minW="175px"
							data-cy="addGroupButton"
							alignSelf="start"
							onClick={() => {
								if (!currentAccount) {
									return;
								}

								openDrawer({
									id: 'groupDrawer',
									content: <GroupDrawer currentAccount={currentAccount} />,
								});
							}}
						>
							{t('contacts.add_group')}
						</Button>
					</Flex>
					<Box flex={1} overflow="auto" minH={0}>
						<DataCollectionTable />
					</Box>
					<Flex gap={2} flexShrink={0}>
						<DataCollectionTableFooter />
						<ConfirmModal ref={confirmRemoveRef} />
					</Flex>
				</DataCollectionContextProvider>
			</Card.Body>
		</Card.Root>
	);
};
